import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { aiLog } from 'src/utils/logger';
import { mpEvent } from 'src/utils/mixpanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';
import useVariables from 'src/hooks/useVariables';
import { useAuth } from 'src/state/auth';
import { showErrorToast, showSuccessToast } from 'src/components/Toast';
import { IntelVariable, RiskIntelSession } from './types';
import { computeFormulaValue } from './util/formula';
import { RiskIntelService } from './service/RiskIntelService';
import { useGraphqlRiskIntelService } from './service/graphql/useGraphqlRiskIntelService';

export interface RiskIntelContextValue {
  session: RiskIntelSession | null;
  pending: boolean;
  /** True when a FE mutation is in flight OR an async rung (frequency/impacts) is generating
   * server-side. Use this to disable action buttons so a click can't race an in-flight rung. */
  busy: boolean;
  resuming: boolean;
  error: string | null;
  /** True when a waiting rung (processing/mining) hasn't progressed within the watchdog window. */
  timedOut: boolean;
  /** Re-fetch the session — recovers a rung whose websocket frame was lost. */
  retrySession: () => void;
  startSession: (userInput: string, workspaceId?: string) => Promise<RiskIntelSession | null>;
  refine: (userInput: string) => Promise<RiskIntelSession | null>;
  selectCycle: (cycleId: string) => Promise<RiskIntelSession | null>;
  chooseFrequency: (chosenValue: number, chosenSourceId?: string) => Promise<RiskIntelSession | null>;
  submitImpacts: (selectedImpactIds: string[]) => Promise<RiskIntelSession | null>;
  changeImpacts: () => Promise<RiskIntelSession | null>;
  acceptImpacts: () => Promise<RiskIntelSession | null>;
  proceedToReview: () => Promise<RiskIntelSession | null>;
  backToIntel: () => Promise<RiskIntelSession | null>;
  populateVariable: (variableId: number, value: number, currency?: boolean) => Promise<RiskIntelSession | null>;
  complete: () => void;
  reset: () => void;
  /** The user's active session when it belongs to a DIFFERENT assessment (one-per-user cap). */
  otherAssessmentSession: RiskIntelSession | null;
  /** Abandon that other-assessment session to free the slot and start fresh here. */
  discardOtherSession: () => Promise<void>;
  service: RiskIntelService;
  assessmentId?: string;
  assessmentName?: string;
}

const RiskIntelContext = createContext<RiskIntelContextValue | null>(null);

interface RiskIntelProviderProps {
  children: React.ReactNode;
  service?: RiskIntelService;
  assessmentId?: string;
  assessmentName?: string;
}

const emitAnalyseCompleted = (result: RiskIntelSession | null, isRefine: boolean) => {
  const latest = result?.cycles[result.cycles.length - 1];
  if (latest?.validation?.state) {
    mpEvent(MPEvents.AiAnalyseCompleted, { validationState: latest.validation.state, isRefine, tags: ['AI', 'WIZARD'] });
  }
};

export const RiskIntelProvider: React.FC<RiskIntelProviderProps> = ({
  children,
  service: serviceProp,
  assessmentId,
  assessmentName,
}) => {
  const { profile } = useAuth();
  const workspaceId = profile?.activeWorkspace?.id;
  const defaultService = useGraphqlRiskIntelService(workspaceId);
  const service = serviceProp ?? defaultService;

  const [session, setSession] = useState<RiskIntelSession | null>(null);
  const [pending, setPending] = useState(false);
  const [resuming, setResuming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [optimisticVars, setOptimisticVars] = useState<Record<number, { value: number; currency: boolean }>>({});
  // The user's one active session when it belongs to a DIFFERENT assessment than this one (the
  // one-session-per-user cap). We don't silently resume it here — see the resume effect.
  const [otherAssessmentSession, setOtherAssessmentSession] = useState<RiskIntelSession | null>(null);

  // Formulas' `#<id>` reference real workspace/assessment vars not carried on the wire session.
  const { variables: rawVariables, invalidate } = useVariables({ assessmentId: assessmentId ?? '', wvOverride: true });
  const [upsertWorkspaceVariables] = useUpsertWorkspaceVariablesMutation();

  const variables = useMemo<IntelVariable[]>(
    () =>
      (rawVariables ?? []).map((v) => {
        const optimistic = optimisticVars[v.id];
        return {
          id: v.id,
          name: v.name,
          value: optimistic ? optimistic.value : v.value,
          scope: v.workspace_variable ? 'workspace' : 'assessment',
          currency: optimistic ? optimistic.currency : v.currency,
        };
      }),
    [rawVariables, optimisticVars],
  );

  const run = useCallback(async (fn: () => Promise<RiskIntelSession>): Promise<RiskIntelSession | null> => {
    setPending(true);
    setError(null);
    try {
      const next = await fn();
      setSession(next);
      return next;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Something went wrong';
      aiLog.error('AI assistant action failed', e);
      setError(message);
      return null;
    } finally {
      setPending(false);
    }
  }, []);

  // FE-local view transitions (changeImpacts / acceptImpacts / proceed / back) do no network, so they
  // skip the `pending`→`busy` flip — otherwise an instant click flashes the whole wizard's controls
  // disabled for a frame.
  const runLocal = useCallback(async (fn: () => Promise<RiskIntelSession>): Promise<RiskIntelSession | null> => {
    setError(null);
    try {
      const next = await fn();
      setSession(next);
      return next;
    } catch (e) {
      aiLog.error('AI assistant local transition failed', e);
      setError(e instanceof Error ? e.message : 'Something went wrong');
      return null;
    }
  }, []);

  const startSession = useCallback(
    async (userInput: string, ws?: string) => {
      const result = await run(() =>
        service.startSession({ userInput, assessmentId, workspaceId: ws ?? workspaceId }),
      );
      emitAnalyseCompleted(result, false);
      return result;
    },
    [run, service, workspaceId, assessmentId],
  );

  const refine = useCallback(
    async (userInput: string) => {
      if (!session) return null;
      const result = await run(() => service.refine(session.id, userInput));
      emitAnalyseCompleted(result, true);
      return result;
    },
    [run, service, session],
  );

  const selectCycle = useCallback(
    (cycleId: string) => (session ? run(() => service.selectCycle(session.id, cycleId)) : Promise.resolve(null)),
    [run, service, session],
  );

  const chooseFrequency = useCallback(
    async (chosenValue: number, chosenSourceId?: string) => {
      if (!session) return null;
      const result = await run(() => service.chooseFrequency(session.id, chosenValue, chosenSourceId));
      if (result) {
        const sourceType = chosenSourceId && chosenSourceId !== 'triangulated' ? 'source' : 'triangulated';
        mpEvent(MPEvents.AiFrequencyChosen, { sourceType, tags: ['AI', 'WIZARD'] });
      }
      return result;
    },
    [run, service, session],
  );

  const submitImpacts = useCallback(
    async (selectedImpactIds: string[]) => {
      if (!session) return null;
      const result = await run(() => service.submitImpacts(session.id, selectedImpactIds));
      if (result) mpEvent(MPEvents.AiImpactsSubmitted, { count: selectedImpactIds.length, tags: ['AI', 'WIZARD'] });
      return result;
    },
    [run, service, session],
  );

  const changeImpacts = useCallback(
    () => (session ? runLocal(() => service.changeImpacts(session.id)) : Promise.resolve(null)),
    [runLocal, service, session],
  );

  const acceptImpacts = useCallback(
    () => (session ? runLocal(() => service.acceptImpacts(session.id)) : Promise.resolve(null)),
    [runLocal, service, session],
  );

  const proceedToReview = useCallback(
    () => (session ? runLocal(() => service.proceedToReview(session.id)) : Promise.resolve(null)),
    [runLocal, service, session],
  );

  const backToIntel = useCallback(
    () => (session ? runLocal(() => service.backToIntel(session.id)) : Promise.resolve(null)),
    [runLocal, service, session],
  );

  const populateVariable = useCallback(
    async (variableId: number, value: number, currency?: boolean): Promise<RiskIntelSession | null> => {
      const v = (rawVariables ?? []).find((x) => x.id === variableId);
      if (!v || !workspaceId) {
        aiLog.warn('populateVariable: unresolved variable / no workspace', { variableId, workspaceId });
        showErrorToast('Could not save the variable');
        return null;
      }
      const resolvedCurrency = currency ?? v.currency;
      setOptimisticVars((m) => ({ ...m, [variableId]: { value, currency: resolvedCurrency } }));
      setPending(true);
      setError(null);
      try {
        await upsertWorkspaceVariables({
          variables: {
            workspaceId,
            variables: [{ id: v.id, name: v.name, value, currency: resolvedCurrency, isGlobal: v.isGlobal }],
          },
        });
        await invalidate();
        showSuccessToast({ title: 'Variable updated', subtitle: v.name });
      } catch (e) {
        aiLog.error('AI populate variable failed', e);
        setError(e instanceof Error ? e.message : 'Could not save the variable');
        showErrorToast('Could not save the variable');
      } finally {
        setOptimisticVars((m) => {
          const next = { ...m };
          delete next[variableId];
          return next;
        });
        setPending(false);
      }
      return null;
    },
    [rawVariables, workspaceId, upsertWorkspaceVariables, invalidate],
  );

  // Resume the user's one in-progress session on mount. There is one active session PER USER (not
  // per assessment), so only adopt it here if it belongs to THIS assessment; a session on another
  // assessment is surfaced separately (otherAssessmentSession) instead of being silently loaded.
  useEffect(() => {
    let cancelled = false;
    service
      .getActiveSession()
      .then((restored) => {
        if (cancelled || !restored) {
          aiLog.info('· resume → none');
          return;
        }
        const matchesAssessment = restored.assessmentId != null && restored.assessmentId === assessmentId;
        if (matchesAssessment) {
          aiLog.info('· resume', `→ ${restored.status}`);
          setSession((prev) => prev ?? restored);
        } else {
          aiLog.info('· resume → active session on another assessment', {
            sessionAssessment: restored.assessmentId,
            current: assessmentId,
          });
          setOtherAssessmentSession(restored);
        }
      })
      .catch((e) => aiLog.error('AI session resume failed', e))
      .finally(() => {
        if (!cancelled) setResuming(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch the session on each update signal; tear the channel down once terminal or it refetches forever.
  const sessionId = session?.id;
  const terminal =
    session?.status === 'completed' || session?.status === 'abandoned' || session?.status === 'errored';
  useEffect(() => {
    if (!sessionId || terminal) return undefined;
    return service.subscribe(sessionId, (e) => {
      aiLog.info('· ws signal → refetch', e);
      service
        .getSession(sessionId)
        .then((next) => {
          if (next) setSession(next);
        })
        .catch((err) => aiLog.error('AI session refetch failed', err));
    });
  }, [sessionId, terminal, service]);

  const status = session?.status;
  useEffect(() => {
    if (!status) return;
    aiLog.info('▸ status', status, { pending, error });
    mpEvent(MPEvents.AiAssistantStep, { step: status, tags: ['AI'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Attach the active AI session (opaque id + current step) to Sentry, so an engine/network failure
  // during the wizard carries which session and stage it hit. Ids + status only — no user input.
  useEffect(() => {
    Sentry.setContext(
      'ai_session',
      session ? { uxId: session.id, status: session.status, selectedCycleId: session.selectedCycleId ?? null } : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.status, session?.selectedCycleId]);
  useEffect(() => () => Sentry.setContext('ai_session', null), []);

  // Watchdog: a rung still waiting (processing/mining) after 90s likely lost its websocket frame or the
  // engine stalled — surface a retry. Keyed on `session` so each refetch restarts the clock.
  const waiting = status === 'processing' || status === 'mining_impacts';
  useEffect(() => {
    if (!waiting) {
      setTimedOut(false);
      return undefined;
    }
    setTimedOut(false);
    const t = setTimeout(() => {
      aiLog.warn('riskIntel: rung timed out (no progress in 90s)', { status });
      setTimedOut(true);
    }, 90_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, session]);

  const retrySession = useCallback(() => {
    if (!sessionId) return;
    aiLog.info('· retry → refetch', { sessionId });
    service
      .getSession(sessionId)
      .then((next) => next && setSession(next))
      .catch((e) => aiLog.error('AI retry refetch failed', e));
  }, [sessionId, service]);

  const complete = useCallback(() => {
    if (session) service.accept(session.id).catch((e) => aiLog.error('AI accept failed', e));
    mpEvent(MPEvents.AiScenarioCreated, { tags: ['AI', 'WIZARD'] });
    setSession((prev) => (prev ? { ...prev, status: 'completed' } : prev));
  }, [session, service]);

  const reset = useCallback(() => {
    if (session) {
      service.abandon(session.id).catch((e) => aiLog.error('AI session abandon failed', e));
      mpEvent(MPEvents.AiSessionAbandoned, { fromStatus: session.status, tags: ['AI', 'WIZARD'] });
    }
    setSession(null);
    setError(null);
  }, [session, service]);

  // Abandon the active session that lives on another assessment, freeing the one-per-user slot so
  // the user can start fresh on THIS assessment.
  const discardOtherSession = useCallback(async () => {
    const other = otherAssessmentSession;
    if (!other) return;
    aiLog.info('→ discard other-assessment session', { sessionId: other.id });
    await service.abandon(other.id).catch((e) => aiLog.error('AI abandon (other assessment) failed', e));
    mpEvent(MPEvents.AiSessionAbandoned, { fromStatus: other.status, tags: ['AI', 'WIZARD'] });
    setOtherAssessmentSession(null);
  }, [otherAssessmentSession, service]);

  // Inject real variables and recompute each impact range; a missing `#<id>` yields null (missing-variable flow).
  const enrichedSession = useMemo<RiskIntelSession | null>(() => {
    if (!session) return null;
    const computeVars = variables.map((v) => ({ id: v.id, value: v.value }));
    return {
      ...session,
      variables,
      impacts: session.impacts?.map((im) =>
        im.formula
          ? {
              ...im,
              formula: {
                ...im.formula,
                computedLower: computeFormulaValue(im.formula.lowerRaw, computeVars),
                computedUpper: computeFormulaValue(im.formula.upperRaw, computeVars),
              },
            }
          : im,
      ),
    };
  }, [session, variables]);

  const value = useMemo<RiskIntelContextValue>(
    () => ({
      session: enrichedSession,
      pending,
      // `!error` so a failed action re-enables the buttons for retry — otherwise a rung that errored
      // while the session sat in processing/mining would leave them stuck disabled.
      busy:
        !error &&
        (pending || enrichedSession?.status === 'processing' || enrichedSession?.status === 'mining_impacts'),
      resuming,
      error,
      timedOut,
      retrySession,
      startSession,
      refine,
      selectCycle,
      chooseFrequency,
      submitImpacts,
      changeImpacts,
      acceptImpacts,
      proceedToReview,
      backToIntel,
      populateVariable,
      complete,
      reset,
      otherAssessmentSession,
      discardOtherSession,
      service,
      assessmentId,
      assessmentName,
    }),
    [
      enrichedSession,
      pending,
      resuming,
      error,
      timedOut,
      retrySession,
      startSession,
      refine,
      selectCycle,
      chooseFrequency,
      submitImpacts,
      changeImpacts,
      acceptImpacts,
      proceedToReview,
      backToIntel,
      populateVariable,
      complete,
      reset,
      otherAssessmentSession,
      discardOtherSession,
      service,
      assessmentId,
      assessmentName,
    ],
  );

  return <RiskIntelContext.Provider value={value}>{children}</RiskIntelContext.Provider>;
};

export const useRiskIntelContext = (): RiskIntelContextValue => {
  const context = useContext(RiskIntelContext);
  if (!context) throw new Error('useRiskIntel must be used within an RiskIntelProvider');
  return context;
};
